import React from 'react';
import { IconButton } from './ui';
import { Menu, User, Coffee, MapPin, PawPrint, LogIn, LogOut, Settings as SettingsIcon } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SearchBar from './SearchBar';
import MobileSearchBar from './MobileSearchBar';
import SearchFilters from './SearchFilters';
import ViewToggle from './ViewToggle';
import LogoIcon from './LogoIcon';
// Lazy-load NotificationBell to prevent Firebase from loading on initial page load
const NotificationBell = React.lazy(() => import('./NotificationBell'));
import { useUser } from '../contexts/useUser';
import { useSearchStore } from '@/stores/searchStore';
import useSuggestDialog from '@/hooks/useSuggestDialog';

interface HeaderProps {
  mode: 'light' | 'dark';
  toggleColorMode: () => void; // Kept for backward compatibility, not used in menu
}

export default function Header({ mode, toggleColorMode }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const resetSearch = useSearchStore(s => s.resetSearch);
  const { openSuggestDialog } = useSuggestDialog();

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = React.useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  React.useEffect(() => {
    setMenuAnchorEl(null);
  }, [location.pathname]);

  const handleLogoClick = () => {
    resetSearch();
    setMenuAnchorEl(null);
    navigate('/');
  };

  const isHome = location.pathname === '/';

  return (
    <>
      {/* Desktop header */}
      <header className="desktop-header hidden sm:flex items-center justify-between px-4 py-2 min-h-[72px] md:min-h-[120px] bg-transparent">
        {/* Logo */}
        <button
          onClick={handleLogoClick}
          className="w-20 h-20 md:w-[120px] md:h-[120px] rounded-full overflow-hidden flex items-center justify-center cursor-pointer bg-transparent border-none p-0"
        >
          <LogoIcon />
        </button>

        {/* Center controls cluster */}
        {isHome && (
          <div
            id="header-controls"
            className="flex items-center justify-center gap-3 mx-auto max-w-[1200px] flex-grow"
          >
            <SearchBar />
            {isHome && (
              <div className="hidden sm:inline-flex ml-2">
                <ViewToggle />
              </div>
            )}
            <SearchFilters forceClose={menuOpen} />
          </div>
        )}

        {/* Right side icons — matches the logo's width (w-20/md:w-[120px]) so the
            center cluster's auto margins split the leftover space evenly and it
            lands truly centered, instead of relying on a hardcoded left offset
            that only looked right at the desktop width it was tuned for. */}
        <div className="flex items-center justify-end gap-2 w-20 md:w-[120px]">
          {user && (
            <React.Suspense fallback={<div />}>
              <NotificationBell />
            </React.Suspense>
          )}
          <IconButton
            aria-label="Open navigation menu"
            onClick={handleMenuOpen}
          >
            <Menu className="w-6 h-6" />
          </IconButton>
        </div>
      </header>

      {/* Mobile header */}
      <header className="mobile-header flex sm:hidden flex-col px-4 py-2 gap-2 bg-transparent">
        <div className="flex items-center justify-between">
          <button
            onClick={handleLogoClick}
            className="flex items-center gap-2.5 cursor-pointer bg-transparent border-none p-0"
            aria-label="Ground Rounds home"
          >
            <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
              <LogoIcon />
            </div>
            <span className="font-caprasimo text-lg text-text-primary leading-tight">
              Ground Rounds
            </span>
          </button>
          <div className="flex items-center gap-2">
            {user && (
              <React.Suspense fallback={<div />}>
                <NotificationBell />
              </React.Suspense>
            )}
          </div>
        </div>
        {isHome && <MobileSearchBar />}
      </header>

      {/* Hamburger menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-50" onClick={handleMenuClose}>
          <div className="absolute right-4 top-20 bg-card-bg rounded-lg shadow-xl min-w-[200px] py-2" onClick={(e) => e.stopPropagation()}>
            {user && (
              <Link to="/profile" onClick={handleMenuClose} className="flex items-center gap-3 px-4 py-3 hover:bg-hover-bg text-text-primary">
                <User className="w-5 h-5" />
                <span>Profile</span>
              </Link>
            )}
            {user && (
              <Link to="/recipes" onClick={handleMenuClose} className="flex items-center gap-3 px-4 py-3 hover:bg-hover-bg text-text-primary">
                <Coffee className="w-5 h-5" />
                <span>Recipes</span>
              </Link>
            )}
            <button onClick={() => { handleMenuClose(); openSuggestDialog(); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-hover-bg text-text-primary text-left">
              <MapPin className="w-5 h-5" />
              <span>Suggest a Place</span>
            </button>
            {user && (
              <Link to="/hounds" onClick={handleMenuClose} className="flex items-center gap-3 px-4 py-3 hover:bg-hover-bg text-text-primary">
                <PawPrint className="w-5 h-5" />
                <span>Hounds</span>
              </Link>
            )}

            {!user && (
              <Link to="/login" onClick={handleMenuClose} className="flex items-center gap-3 px-4 py-3 hover:bg-hover-bg text-text-primary">
                <LogIn className="w-5 h-5" />
                <span>Login</span>
              </Link>
            )}

            <Link to="/settings" onClick={handleMenuClose} className="flex items-center gap-3 px-4 py-3 hover:bg-hover-bg text-text-primary">
              <SettingsIcon className="w-5 h-5" />
              <span>Settings</span>
            </Link>

            {user && (
              <button
                onClick={async () => {
                  handleMenuClose();
                  await logout();
                  window.location.href = '/login';
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-hover-bg text-text-primary text-left"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
